import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
/**
 * Rangée qui défile toute seule (cartes de prix, avis) ET à la main.
 * - Par défaut, elle avance lentement, en boucle et sans à-coup (le contenu est affiché deux fois).
 * - Souris au-dessus, toucher ou glisser : pause. On peut alors faire défiler à la molette / au pavé tactile,
 *   en glissant à la souris, au doigt, ou avec les flèches (<RowArrows />).
 * - Le défilement automatique reprend 2 s après le départ de la souris (4 s après un clic sur une flèche).
 * - Réglage « réduire les animations » du système : pas de défilement automatique.
 *
 *   const rowRef = useRef(null);
 *   <RowArrows onPrev={() => rowRef.current?.prev()} onNext={() => rowRef.current?.next()} />
 *   <AutoScrollRow ref={rowRef} items={cards} getKey={(c) => c.key} renderItem={(c) => <Card … />} />
 */
const AutoScrollRow = forwardRef(function AutoScrollRow(
  { items = [], renderItem, getKey = (x, i) => i, speed = 30, className = "", testid },
  ref
) {
  const rowRef = useRef(null);
  const st = useRef({ paused: false, resumeAt: 0, drag: null, acc: 0 });
  useEffect(() => {
    const el = rowRef.current;
    if (!el || !items.length) return undefined;
    const s = st.current;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const half = () => el.scrollWidth / 2;
    // Boucle infinie : au-delà de la première copie, on revient d'autant en arrière (invisible à l'écran)
    const wrap = () => {
      const h = half();
      if (h <= el.clientWidth) return;
      if (el.scrollLeft >= h) el.scrollLeft -= h;
      else if (el.scrollLeft <= 0) el.scrollLeft += h;
    };
    const pause = () => { s.paused = true; };
    const resume = () => { s.paused = false; s.resumeAt = performance.now() + 2000; };
    const onScroll = () => { if (s.paused || s.drag) wrap(); };
    const onDown = (e) => {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      s.drag = { x: e.clientX, left: el.scrollLeft, moved: false };
    };
    const onMove = (e) => {
      if (!s.drag) return;
      const dx = e.clientX - s.drag.x;
      if (!s.drag.moved && Math.abs(dx) > 5) { s.drag.moved = true; el.style.cursor = "grabbing"; }
      if (s.drag.moved) el.scrollLeft = s.drag.left - dx;
    };
    const onUp = () => {
      if (!s.drag) return;
      const moved = s.drag.moved;
      s.drag = null;
      el.style.cursor = "";
      // le clic qui termine un glissement n'ouvre pas la carte
      if (moved) el.addEventListener("click", (ev) => { ev.preventDefault(); ev.stopPropagation(); }, { capture: true, once: true });
    };
    el.addEventListener("mouseenter", pause);
    el.addEventListener("mouseleave", resume);
    el.addEventListener("touchstart", pause, { passive: true });
    el.addEventListener("touchend", resume);
    el.addEventListener("focusin", pause);
    el.addEventListener("focusout", resume);
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    let last = performance.now();
    let raf = 0;
    const tick = (t) => {
      const dt = Math.min(64, t - last);
      last = t;
      if (!reduce && !s.paused && !s.drag && t >= s.resumeAt && !document.hidden) {
        s.acc += (speed * dt) / 1000;
        const step = Math.floor(s.acc);
        if (step) {
          el.scrollLeft += step;
          s.acc -= step;
          wrap();
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("mouseenter", pause);
      el.removeEventListener("mouseleave", resume);
      el.removeEventListener("touchstart", pause);
      el.removeEventListener("touchend", resume);
      el.removeEventListener("focusin", pause);
      el.removeEventListener("focusout", resume);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [items.length, speed]);
  const by = useCallback((dir) => {
    const el = rowRef.current;
    if (!el) return;
    const s = st.current;
    s.resumeAt = performance.now() + 4000;
    const h = el.scrollWidth / 2;
    if (h > el.clientWidth) {
      if (dir < 0 && el.scrollLeft < el.clientWidth) el.scrollLeft += h;
      if (dir > 0 && el.scrollLeft > h - el.clientWidth) el.scrollLeft -= h;
    }
    const card = el.firstElementChild;
    const step = card ? card.getBoundingClientRect().width + 16 : 300;
    el.scrollBy({ left: dir * step * Math.max(1, Math.floor(el.clientWidth / step) - 1), behavior: "smooth" });
  }, []);
  useImperativeHandle(ref, () => ({ prev: () => by(-1), next: () => by(1) }), [by]);
  const mask = "linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent)";
  return (
    <div
      ref={rowRef}
      className={`flex gap-4 overflow-x-auto scrollbar-none py-1 select-none ${className}`}
      style={{ WebkitMaskImage: mask, maskImage: mask }}
      data-testid={testid}
    >
      {[...items, ...items].map((item, idx) => (
        <div key={`${getKey(item, idx % items.length)}-${idx}`} className="shrink-0" aria-hidden={idx >= items.length ? "true" : undefined}>
          {renderItem(item, idx)}
        </div>
      ))}
    </div>
  );
});
export default AutoScrollRow;
/** Flèches précédent / suivant d'une rangée. */
export function RowArrows({ onPrev, onNext, testid = "row" }) {
  const cls = "grid place-items-center w-9 h-9 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white transition-colors";
  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={onPrev} className={cls} aria-label="Précédent" data-testid={`${testid}-prev`}>
        <ChevronLeft className="w-4 h-4" />
      </button>
      <button type="button" onClick={onNext} className={cls} aria-label="Suivant" data-testid={`${testid}-next`}>
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}
